import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import { TypeGuardError } from 'src/core/utils/switchGuard';

import {
  type ApprovalWorkflowExport,
  type ApprovalWorkflowBranchExport,
  type ApprovalSchemeExport,
  type ApprovalWorkflowValuesExport,
} from './approvalWorkflowExportTypes';
import { collectDimensions, getDimensionNameKey } from './dimension';
import { ensureOtherValuesNodeLast } from './ensureOtherValuesNodeLast';
import { formatApprovalScheme } from './formatApprovalScheme';
import { type DimensionName } from '../../approvalWorkflow';
import {
  type Table,
  type TableCell,
  type TableRow,
  buildCell,
  buildEmptyRow,
  buildRow,
  buildTable,
} from '../spreadsheet/spreadsheetModel';

const formatHeaderForDimension = (
  t: TGlobalFunctionTyped,
  dimension: DimensionName,
  type: 'when' | 'and',
): string => {
  switch (dimension.type) {
    case 'costCenter':
      return type === 'when'
        ? t('approvalWorkflows.export.dimensions.costCenter.whenIsOneOf')
        : t('approvalWorkflows.export.dimensions.costCenter.andIsOneOf');
    case 'spendType':
      return type === 'when'
        ? t('approvalWorkflows.export.dimensions.spendType.whenIsOneOf')
        : t('approvalWorkflows.export.dimensions.spendType.andIsOneOf');
    case 'team':
      return type === 'when'
        ? t('approvalWorkflows.export.dimensions.team.whenIsOneOf')
        : t('approvalWorkflows.export.dimensions.team.andIsOneOf');
    case 'expenseCategory':
      return type === 'when'
        ? t('approvalWorkflows.export.dimensions.expenseCategory.whenIsOneOf')
        : t('approvalWorkflows.export.dimensions.expenseCategory.andIsOneOf');
    case 'customField':
      return type === 'when'
        ? t('approvalWorkflows.export.dimensions.customField.whenIsOneOf', {
            customFieldName: dimension.name,
          })
        : t('approvalWorkflows.export.dimensions.customField.andIsOneOf', {
            customFieldName: dimension.name,
          });
    default:
      throw new TypeGuardError(dimension, 'Unknown dimension type');
  }
};

const buildDimensionHeader = (
  t: TGlobalFunctionTyped,
  dimension: DimensionName,
  index: number,
): TableCell => {
  const content = formatHeaderForDimension(
    t,
    dimension,
    index === 0 ? 'when' : 'and',
  );
  return buildCell({ content });
};

type DimensionValues = Map<
  string,
  { dimension: DimensionName; values: ApprovalWorkflowValuesExport }
>;

const buildValuesCell = (
  values: ApprovalWorkflowValuesExport,
  t: TGlobalFunctionTyped,
): TableCell => {
  if (values === null) {
    return buildCell({
      content: t('approvalWorkflows.export.otherValues'),
      bold: true,
    });
  }
  if (values.length === 0) {
    return buildCell();
  }
  return buildCell({ content: values.join('\n') });
};

export const exportApprovalWorkflowAsStructuredTable = (
  workflow: ApprovalWorkflowExport,
  t: TGlobalFunctionTyped,
): Table => {
  const dimensions = collectDimensions(workflow.branch);

  const rows: TableRow[] = [];

  // Header row
  const headerRow = buildRow([
    buildCell({ content: t('approvalWorkflows.export.approvalWorkflow') }),
    ...dimensions
      .values()
      .map((dimension, index) => buildDimensionHeader(t, dimension, index)),
    buildCell({ content: t('approvalWorkflows.export.thenApprovalSchemeIs') }),
    buildCell({ content: t('approvalWorkflows.export.defaultApprovalScheme') }),
  ]);
  rows.push(headerRow);

  const columnIndexByDimension = new Map(
    [...dimensions.keys()].map((dimensionKey, index) => [
      dimensionKey,
      index + 1,
    ]),
  );
  const rowSize = headerRow.cells.length;
  const approvalSchemeColumnIndex = rowSize - 2;
  const defaultApprovalSchemeColumnIndex = rowSize - 1;

  // Default approval scheme row
  const defaultApprovalSchemeRow = buildEmptyRow(rowSize);
  defaultApprovalSchemeRow.cells[defaultApprovalSchemeColumnIndex] = buildCell({
    content:
      workflow.defaultApprovalScheme &&
      formatApprovalScheme(workflow.defaultApprovalScheme, t),
  });
  rows.push(defaultApprovalSchemeRow);

  const buildPathRow = (
    path: DimensionValues,
    scheme?: ApprovalSchemeExport,
  ): TableRow => {
    const row = buildEmptyRow(rowSize);
    path.forEach(({ values }, dimensionKey) => {
      const columnIndex = columnIndexByDimension.get(dimensionKey);
      if (columnIndex !== undefined) {
        row.cells[columnIndex] = buildValuesCell(values, t);
      }
    });
    row.cells[approvalSchemeColumnIndex] = buildCell({
      content: scheme ? formatApprovalScheme(scheme, t) : undefined,
    });
    row.cells[defaultApprovalSchemeColumnIndex] = buildCell({
      content: scheme
        ? undefined
        : t('approvalWorkflows.export.useDefaultScheme'),
    });
    return row;
  };

  // - at each level keep the approval scheme
  // - when reaching a leaf record a row
  const traverseBranch = (
    path: DimensionValues,
    branch?: ApprovalWorkflowBranchExport,
    scheme?: ApprovalSchemeExport,
  ) => {
    if (!branch) {
      rows.push(buildPathRow(path, scheme));
      return;
    }
    ensureOtherValuesNodeLast(branch.nodes).forEach((node) => {
      const nodePath = new Map(path);
      nodePath.set(getDimensionNameKey(branch.dimension), {
        dimension: branch.dimension,
        values: node.values,
      });
      traverseBranch(nodePath, node.branch, node.approvalScheme ?? scheme);
    });
  };

  traverseBranch(new Map(), workflow.branch);

  return buildTable({ headerRowCount: 1, frozenRowCount: 1, rows });
};
